import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  Clock,
  FileEdit,
  FileText,
  FolderOpen,
  Loader2,
  Search,
  Sparkles,
  Terminal,
} from 'lucide-react';

import type { ChatContentBlock } from '@/hooks/useWebSocket';
import { cn } from '@/lib/utils';
import {
  formatElapsed,
  getToolDescription,
  getToolPresentation,
  type ToolPresentationKind,
} from './presentation';

interface AgentActivityProps {
  contentBlocks: ChatContentBlock[];
  elapsed?: number;
  lastActivityTime?: number | null;
}

const STALENESS_THRESHOLD_MS = 15_000;

function ToolIcon({ kind }: { kind: ToolPresentationKind }): ReactNode {
  const className = 'h-3.5 w-3.5';
  if (kind === 'read') return <FileText className={className} />;
  if (kind === 'write') return <FileEdit className={className} />;
  if (kind === 'run') return <Terminal className={className} />;
  if (kind === 'search') return <Search className={className} />;
  if (kind === 'list') return <FolderOpen className={className} />;
  return <Clock className={className} />;
}

export function AgentActivity({ contentBlocks, elapsed = 0, lastActivityTime }: AgentActivityProps) {
  const [expanded, setExpanded] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const elapsedAnchorRef = useRef({ serverElapsed: elapsed, receivedAt: Date.now() });

  useEffect(() => {
    const receivedAt = Date.now();
    elapsedAnchorRef.current = { serverElapsed: elapsed, receivedAt };
    setNow(receivedAt);
  }, [elapsed]);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(interval);
  }, []);

  const toolCalls = useMemo(() => {
    const completedIds = new Set(
      contentBlocks
        .filter((block) => block.type === 'tool_result' && block.tool_use_id)
        .map((block) => block.tool_use_id as string)
    );
    return contentBlocks
      .filter((block) => block.type === 'tool_use' && block.id && block.name)
      .map((block) => ({
        id: block.id as string,
        name: block.name as string,
        input: block.input,
        isComplete: completedIds.has(block.id as string),
      }));
  }, [contentBlocks]);

  if (toolCalls.length === 0) return null;

  const completedCount = toolCalls.filter((tool) => tool.isComplete).length;
  const activeCount = toolCalls.length - completedCount;
  const displayElapsed = Math.max(
    0,
    elapsedAnchorRef.current.serverElapsed + now - elapsedAnchorRef.current.receivedAt
  );
  const isStale = Boolean(lastActivityTime && now - lastActivityTime > STALENESS_THRESHOLD_MS);

  return (
    <div className="mb-4 max-w-2xl">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((current) => !current)}
        className="-ml-1.5 flex items-center gap-2 rounded-lg px-1.5 py-1 text-left text-[13px] text-surface-600 transition-colors hover:bg-surface-100 hover:text-surface-900 dark:text-surface-400 dark:hover:bg-surface-800 dark:hover:text-surface-100"
      >
        <Sparkles className={cn('h-4 w-4', activeCount > 0 ? 'animate-pulse text-primary-600 dark:text-primary-400' : 'text-surface-400')} />
        <span className="font-medium" role="status">
          {activeCount > 0 ? 'Working' : 'Activity'}
        </span>
        <span className="tabular-nums text-surface-400 dark:text-surface-500">
          {toolCalls.length} {toolCalls.length === 1 ? 'tool call' : 'tool calls'} · {formatElapsed(displayElapsed)}
        </span>
        <ChevronDown className={cn('h-3.5 w-3.5 text-surface-400 transition-transform duration-200', expanded && 'rotate-180')} />
      </button>

      {isStale && (
        <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
          <span>Connection may be lost. If this persists, try refreshing.</span>
        </div>
      )}

      <div
        className="grid transition-[grid-template-rows,opacity] duration-300"
        style={{ gridTemplateRows: expanded ? '1fr' : '0fr', opacity: expanded ? 1 : 0 }}
      >
        <div className="overflow-hidden">
          <div className="ml-1 mt-1.5 border-l border-surface-200 pl-3 dark:border-surface-700">
            {toolCalls.map((tool) => {
              const { kind, label } = getToolPresentation(tool.name);
              return (
                <div key={tool.id} className="flex min-h-8 items-center gap-2 rounded-lg px-1.5 text-xs text-surface-600 dark:text-surface-400">
                  <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center text-surface-400">
                    {tool.isComplete ? <Check className="h-3.5 w-3.5" /> : <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  </span>
                  <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center text-surface-400"><ToolIcon kind={kind} /></span>
                  <span className="font-medium text-surface-700 dark:text-surface-300">{label}</span>
                  <span className="min-w-0 truncate font-mono text-[11px] text-surface-500 dark:text-surface-500">
                    {getToolDescription(tool.name, tool.input)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
